import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore.js';
import { ViewerBanner } from './ViewerBanner.js';
import { PricingAlertBanner } from './PricingAlertBanner.js';
import { SubscriptionExpiredBanner } from './SubscriptionExpiredBanner.js';

interface Props {
  children: ReactNode;
  /** Si se especifica, valida que el tenant tenga ese programa contratado. */
  requireProgram?: string;
  /**
   * Pantallas que el invitado del sitio demo NO debe alcanzar, ni escribiendo la URL:
   * las de cuenta, configuración y soporte interno. No son parte del recorrido del
   * demo y muestran datos de la sesión —el rol, la empresa, el usuario— que a un
   * visitante anónimo no le dicen nada y no le corresponden. El servidor ya lo frena
   * por su lista blanca de rutas; esto es la otra mitad, para que además no se vea.
   */
  soloUsuarioReal?: boolean;
  /**
   * Pantallas de soporte interno de Ecosoft. Un cliente no las puede ver ni
   * escribiendo la URL: no son suyas y muestran plomería (claves de calibración,
   * simulación de planes, consumo).
   *
   * Va en la RUTA a propósito. Antes cada pantalla se protegía sola y por eso dos
   * se quedaron sin portero (`/staff/context-help` y `/staff/adlic-sim`): un
   * cliente que escribía la URL las veía. Acá no se puede olvidar — una pantalla
   * nueva bajo /staff sin esta marca se nota leyendo el router. Las pantallas que
   * ya traen su propio chequeo lo conservan: dos cerraduras no molestan.
   */
  soloStaff?: boolean;
}

export function ProtectedRoute({ children, requireProgram, soloUsuarioReal, soloStaff }: Props) {
  const location = useLocation();
  const { status, programs, demoGuest, esStaff } = useAuthStore((s) => ({
    status: s.status,
    programs: s.programs,
    demoGuest: s.demoGuest,
    // Mismo criterio que `useIsEcosoftStaff` y que las rutas /staff/* del API.
    esStaff: !!s.tenant?.isEcosoftOwned && (s.tenant.role === 'admin' || s.tenant.role === 'owner'),
  }));

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireProgram && !programs.some((p) => p.programId === requireProgram)) {
    return <Navigate to="/no-access" replace />;
  }

  if (demoGuest && soloUsuarioReal) {
    return <Navigate to="/projects" replace />;
  }

  if (soloStaff && !esStaff) {
    return <Navigate to="/projects" replace />;
  }

  // En el sitio demo no molestamos con banners de cuota / suscripción / viewer.
  // El asistente vive en el header (DemoGuide dentro de AppTopBar).
  // Avisos de tokens 75/90/100: TokenAlertBanner, debajo de la barra (AppTopBar).
  if (demoGuest) {
    return <>{children}</>;
  }

  return (
    <>
      <SubscriptionExpiredBanner />
      <ViewerBanner />
      <PricingAlertBanner />
      {children}
    </>
  );
}
